/** XeAgents.ai - Elite Vending Machine Inventory Alerts **/
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertCircle,
  Bell,
  BellOff,
  Package,
  TrendingDown,
  Phone,
  Mail,
  MessageSquare,
  Settings,
  CheckCircle,
  RefreshCw
} from "lucide-react";
import { useTenant } from "@/components/TenantProvider";

// Mock data for demonstration
const mockAlerts = [
  { id: 1, machineId: "EVM-004", product: "Coca-Cola", currentStock: 3, threshold: 10, severity: "critical", triggered: "15 mins ago" },
  { id: 2, machineId: "EVM-002", product: "Snickers Bar", currentStock: 8, threshold: 15, severity: "warning", triggered: "1 hour ago" },
  { id: 3, machineId: "EVM-004", product: "Water Bottle", currentStock: 5, threshold: 10, severity: "warning", triggered: "2 hours ago" },
  { id: 4, machineId: "EVM-001", product: "Chips", currentStock: 12, threshold: 15, severity: "info", triggered: "3 hours ago" },
  { id: 5, machineId: "EVM-003", product: "Energy Drink", currentStock: 4, threshold: 8, severity: "critical", triggered: "4 hours ago" },
];

const mockAlertRules = [
  { id: 1, name: "Critical Stock Alert", threshold: 10, notifyPhone: true, notifyEmail: true, notifySms: false, enabled: true },
  { id: 2, name: "Low Stock Warning", threshold: 25, notifyPhone: false, notifyEmail: true, notifySms: false, enabled: true },
  { id: 3, name: "Restock Reminder", threshold: 40, notifyPhone: false, notifyEmail: true, notifySms: false, enabled: false },
];

function getSeverityBadge(severity: string) {
  switch (severity) {
    case "critical":
      return <Badge className="bg-red-500"><AlertCircle className="h-3 w-3 mr-1" />Critical</Badge>;
    case "warning":
      return <Badge className="bg-yellow-500"><TrendingDown className="h-3 w-3 mr-1" />Warning</Badge>;
    case "info":
      return <Badge className="bg-blue-500">Info</Badge>;
    default:
      return <Badge>{severity}</Badge>;
  }
}

export default function InventoryAlerts() {
  const { config } = useTenant();
  const [alertRules, setAlertRules] = useState(mockAlertRules);

  const toggleRule = (ruleId: number) => {
    setAlertRules(rules =>
      rules.map(rule =>
        rule.id === ruleId ? { ...rule, enabled: !rule.enabled } : rule
      )
    );
  };

  const criticalCount = mockAlerts.filter(a => a.severity === "critical").length;
  const warningCount = mockAlerts.filter(a => a.severity === "warning").length;

  return (
    <div className="flex-1 space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Inventory Alerts</h1>
          <p className="text-muted-foreground">
            Monitor stock levels and configure automated alerts
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button>
            <Settings className="h-4 w-4 mr-2" />
            Configure Alerts
          </Button>
        </div>
      </div>

      {/* Alert Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className={criticalCount > 0 ? "border-red-500 border-2" : ""}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Critical Alerts</CardTitle>
            <AlertCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">{criticalCount}</div>
            <p className="text-xs text-muted-foreground">
              Immediate action required
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Warnings</CardTitle>
            <TrendingDown className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-500">{warningCount}</div>
            <p className="text-xs text-muted-foreground">
              Stock running low
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Rules</CardTitle>
            <Bell className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {alertRules.filter(r => r.enabled).length}/{alertRules.length}
            </div>
            <p className="text-xs text-muted-foreground">
              Alert rules enabled
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Active Alerts */}
      <Card>
        <CardHeader>
          <CardTitle>Active Alerts</CardTitle>
          <CardDescription>
            Current inventory alerts requiring attention
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Severity</TableHead>
                <TableHead>Machine</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Current Stock</TableHead>
                <TableHead>Threshold</TableHead>
                <TableHead>Triggered</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mockAlerts.map((alert) => (
                <TableRow key={alert.id}>
                  <TableCell>{getSeverityBadge(alert.severity)}</TableCell>
                  <TableCell className="font-medium">{alert.machineId}</TableCell>
                  <TableCell>{alert.product}</TableCell>
                  <TableCell>
                    <span className={
                      alert.currentStock <= 5 ? "text-red-500 font-bold" :
                      alert.currentStock <= 10 ? "text-yellow-500 font-semibold" : ""
                    }>
                      {alert.currentStock} units
                    </span>
                  </TableCell>
                  <TableCell>{alert.threshold} units</TableCell>
                  <TableCell>{alert.triggered}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Acknowledge
                      </Button>
                      <Button variant="outline" size="sm">
                        Schedule Restock
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Alert Rules Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>Alert Rules</CardTitle>
          <CardDescription>
            Configure when and how you receive inventory alerts
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {alertRules.map((rule) => (
              <div
                key={rule.id}
                className={`flex items-center justify-between p-4 border rounded-lg ${
                  rule.enabled ? "bg-background" : "bg-muted/50"
                }`}
              >
                <div className="flex items-center gap-4">
                  <Switch
                    checked={rule.enabled}
                    onCheckedChange={() => toggleRule(rule.id)}
                  />
                  <div>
                    <div className="font-medium">{rule.name}</div>
                    <div className="text-sm text-muted-foreground">
                      Trigger when stock falls below {rule.threshold}%
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex gap-2">
                    {rule.notifyPhone && (
                      <Badge variant="outline">
                        <Phone className="h-3 w-3 mr-1" />
                        Call
                      </Badge>
                    )}
                    {rule.notifyEmail && (
                      <Badge variant="outline">
                        <Mail className="h-3 w-3 mr-1" />
                        Email
                      </Badge>
                    )}
                    {rule.notifySms && (
                      <Badge variant="outline">
                        <MessageSquare className="h-3 w-3 mr-1" />
                        SMS
                      </Badge>
                    )}
                  </div>
                  <Button variant="ghost" size="sm">
                    Edit
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <Button variant="outline">
              <Bell className="h-4 w-4 mr-2" />
              Add New Rule
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Outbound Call Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>Automated Outbound Calls</CardTitle>
          <CardDescription>
            Configure AI-powered calls to location partners when restocking is needed
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Call Trigger Threshold</Label>
              <Select defaultValue="critical">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="critical">Critical alerts only</SelectItem>
                  <SelectItem value="warning">Warning and above</SelectItem>
                  <SelectItem value="all">All alerts</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Call Script</Label>
              <Select defaultValue="restock">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="restock">Restock Notification</SelectItem>
                  <SelectItem value="urgent">Urgent Restock</SelectItem>
                  <SelectItem value="custom">Custom Script</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-4">
            <Switch id="auto-call" />
            <Label htmlFor="auto-call">Enable automated outbound calls for inventory alerts</Label>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
